/**
 * Per-transaction / selected-transactions export control (MOR-808).
 *
 * The workspace-level `VoidableTransactionsExportButton` exports every claim
 * as a workbook. Jeff Shute asked to "just print this one preference" and save
 * it to file, so this control exports a single flagged transaction — or the
 * rows the practitioner has ticked — as a file-note style Excel / Word / PDF.
 *
 * It reuses `listFlagsForExport` (the same query the workspace export runs, so
 * convexQuery dedupes the subscription) and filters to the requested flags
 * client-side. The query only mounts once the popover is opened, so putting an
 * instance on every table row costs nothing until it's used.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { saveAs } from "file-saver";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { exportToExcel, exportToWord, exportToPdf } from "@/utils/export";
import {
  buildFlagNoteExport,
  flagNoteFileName,
  type FlagExportRow,
} from "./buildVoidableTransactionsExport";

type ExportFormat = "excel" | "word" | "pdf";

const FORMATS: Array<{
  key: ExportFormat;
  label: string;
  ext: string;
  icon: typeof FileText;
}> = [
  { key: "excel", label: "Excel", ext: "xlsx", icon: FileSpreadsheet },
  { key: "word", label: "Word", ext: "docx", icon: FileText },
  { key: "pdf", label: "PDF", ext: "pdf", icon: FileText },
];

export function FlagExportMenu(props: {
  jobId: Id<"jobs">;
  /** Flags to export, in the order they should read in the document. */
  flagIds: string[];
  /** "icon" is the compact row action; "button" is the labelled control. */
  variant?: "icon" | "button";
  /** Override the labelled variant's text (defaults to "Export"). */
  label?: string;
  className?: string;
}) {
  const { jobId, flagIds, variant = "button", label, className } = props;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data } = useQuery({
    ...convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .listFlagsForExport,
      { jobId },
    ),
    // Only subscribe once the practitioner actually opens the menu.
    enabled: open,
  });
  const allFlags = data as FlagExportRow[] | undefined;

  // Keep the caller's order (row order / selection order), not the query's.
  const flags = useMemo(() => {
    if (!allFlags) return undefined;
    const byId = new Map(allFlags.map((f) => [f.flagId, f]));
    return flagIds
      .map((id) => byId.get(id))
      .filter((f): f is FlagExportRow => f !== undefined);
  }, [allFlags, flagIds]);

  const handleExport = async ({
    key,
    ext,
  }: {
    key: ExportFormat;
    ext: string;
  }) => {
    if (!flags || flags.length === 0) return;
    setBusy(key);
    try {
      const content = buildFlagNoteExport(flags);
      const blob =
        key === "excel"
          ? exportToExcel(content)
          : key === "word"
            ? await exportToWord(content)
            : await exportToPdf(content);
      saveAs(blob, `${flagNoteFileName(flags)}.${ext}`);
      setOpen(false);
    } catch (err) {
      console.error(`Voidable transaction export (${key}) failed:`, err);
      toast.error("Export failed — please try again");
    } finally {
      setBusy(null);
    }
  };

  const loading = open && flags === undefined;
  // The flag was exported/deleted between the table read and the export read.
  const missing = flags !== undefined && flags.length === 0;
  const count = flagIds.length;
  const title =
    count === 1
      ? "Export this transaction"
      : `Export ${count} selected transactions`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === "icon" ? (
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 w-7 p-0", className)}
            title={title}
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={cn("h-7 gap-1 text-xs", className)}
            title={title}
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3 w-3" />
            {label ?? "Export"}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-medium text-foreground">
          {count === 1 ? "Export transaction" : `Export ${count} transactions`}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {missing
            ? "This transaction is no longer available to export."
            : "Transaction detail, why it was flagged, defences, and evidence."}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={loading || missing || busy !== null}
              onClick={() => void handleExport(f)}
            >
              {busy === f.key || (loading && busy === null) ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <f.icon className="h-3.5 w-3.5" />
              )}
              {f.label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
